import React from "react";
import { generateHTML } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import ImageExt from "@tiptap/extension-image";
import { ICON_MAP } from "./WikiIcons";
import { FileText, Clock, User } from "lucide-react";

export default function WikiViewer({ page }) {
    const html = React.useMemo(() => {
        if (!page.body) return "<p class='opacity-40 italic'>This page is empty.</p>";
        try {
            const json = JSON.parse(page.body);
            return generateHTML(json, [
                StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
                Link.configure({ openOnClick: true }),
                Underline,
                Highlight,
                TaskList,
                TaskItem.configure({ nested: true }),
                Table,
                TableRow,
                TableCell,
                TableHeader,
                ImageExt,
            ]);
        } catch {
            return page.body || "<p class='opacity-40 italic'>This page is empty.</p>";
        }
    }, [page.body]);

    const Icon = ICON_MAP[page.icon] || FileText;

    return (
        <div>
            {/* Page header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="bg-[#E8241A] brut-border p-2">
                        <Icon size={24} strokeWidth={2.5} className="text-white" />
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl leading-tight">
                        {page.title}
                    </h1>
                </div>
                <div className="flex items-center gap-4 text-xs font-mono opacity-50 mt-2">
                    <span className="flex items-center gap-1">
                        <User size={12} />
                        {page.updated_by}
                    </span>
                    {page.updated_at && (
                        <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {new Date(page.updated_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </span>
                    )}
                </div>
            </div>

            {/* Rendered content */}
            <div
                className="wiki-viewer prose prose-neutral max-w-none"
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
}
